import { MMKV } from 'react-native-mmkv';

import type { ChecklistValueV2RpcInput } from '../../types/syncRecovery';
import type { ChecklistValuesV2PreparedStore } from './ChecklistValuesV2Service';

interface PersistedPreparedValue {
  fingerprint: string;
  payload: ChecklistValueV2RpcInput;
  dispatched?: boolean;
  operation?: { id: string; timestamp: number; data: unknown };
  expectedObservation?: string | null;
  observationBasis?: Record<string, unknown>;
}

const storage = new MMKV({ id: 'app-guincho-checklist-values-v2-prepared' });

const storageKey = (operationId: string, fieldId: string): string =>
  `prepared:${encodeURIComponent(operationId)}:${encodeURIComponent(fieldId)}`;

export class MMKVChecklistValuesV2PreparedStore implements ChecklistValuesV2PreparedStore {
  /** Ausência difere de corrupção; nenhuma tentativa enviada permite trocar identidade ou CAS. */
  canAdoptCanonicalMedia(operationId: string, fieldIds: readonly string[], valueIds: readonly string[]): boolean {
    try {
      for (const key of storage.getAllKeys()) {
        if (!key.startsWith('prepared:')) continue;
        const parts = key.split(':');
        const keyMatches = parts[1] === encodeURIComponent(operationId) ||
          fieldIds.some(id => parts[2] === encodeURIComponent(id));
        let parsed: PersistedPreparedValue;
        try { parsed = JSON.parse(storage.getString(key) ?? ''); } catch { if (keyMatches) return false; else continue; }
        const payload = parsed?.payload;
        const related = keyMatches || Boolean(payload && (valueIds.includes(payload.id) ||
          fieldIds.includes(payload.campo_id ?? '') || fieldIds.includes(payload.snapshot_field_id ?? '')));
        if (!related) continue;
        if (parts.length !== 3 || !parsed || typeof parsed.fingerprint !== 'string' || !parsed.fingerprint ||
            !payload || typeof payload.id !== 'string' || !payload.id || typeof payload.idempotency_key !== 'string' ||
            !payload.idempotency_key || payload.campo_id !== null && typeof payload.campo_id !== 'string' ||
            payload.snapshot_field_id !== null && typeof payload.snapshot_field_id !== 'string' ||
            payload.valor !== null && typeof payload.valor !== 'string' ||
            payload.expected_updated_at !== null && (typeof payload.expected_updated_at !== 'string' ||
              !Number.isFinite(Date.parse(payload.expected_updated_at))) ||
            parsed.dispatched !== false ||
            parsed.operation !== undefined || parsed.observationBasis !== undefined) return false;
      }
      return true;
    } catch { return false; }
  }

  get(operationId: string, fieldId: string): PersistedPreparedValue | null {
    const raw = storage.getString(storageKey(operationId, fieldId));
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as PersistedPreparedValue;
      return parsed?.fingerprint && parsed?.payload?.idempotency_key ? parsed : null;
    } catch {
      return null;
    }
  }

  set(operationId: string, fieldId: string, value: PersistedPreparedValue): void {
    const key = storageKey(operationId, fieldId);
    const knownUnsent = !storage.contains(key) || this.get(operationId, fieldId)?.dispatched === false;
    // Registros antigos sem tracking continuam desconhecidos; leitura não prova ausência de envio.
    storage.set(key, JSON.stringify(value.dispatched === undefined && knownUnsent ? { ...value, dispatched: false } : value));
  }

  delete(operationId: string, fieldId: string): void {
    storage.delete(storageKey(operationId, fieldId));
  }

  markDispatched(operationId: string, fieldId: string, idempotencyKey: string,
    operation: { id: string; timestamp: number; data: unknown }, expectedObservation?: string | null, observationBasis?: Record<string, unknown>): void {
    const prepared = this.get(operationId, fieldId);
    if (prepared?.payload.idempotency_key === idempotencyKey) this.set(operationId, fieldId,
      { ...prepared, dispatched: true, operation: prepared.operation ?? operation,
        expectedObservation: prepared.dispatched ? prepared.expectedObservation : expectedObservation,
        observationBasis: prepared.dispatched ? prepared.observationBasis : observationBasis });
  }
}
